import { Box, Drawer, Link, Stack, Tooltip, Typography } from '@mui/material'
import { DataGrid, type GridColumns, GridOverlay } from '@mui/x-data-grid'
import { matchSorter } from 'match-sorter'
import React, { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

import { SearchField } from 'components/common/SearchField'
import { TruncatedTextWithTooltip } from 'components/common/TruncatedTextWithTooltip'
import { ManageClusterAccessDrawerContent } from 'components/configurations/clusters/manage-cluster-access/ManageClusterAccessDrawerContent'
import { SEARCH_INPUT_FIELD_WIDTH } from 'components/configurations/configurations-utils'
import Icon from 'components/Icon'
import {
  SEARCH_PARAMS_KEY,
  useDebouncedSearchParams
} from 'hooks/use-debounced-search-params'
import { useDrawerControlForDataGrid } from 'hooks/use-drawer-control-for-data-grid'
import {
  type TeamWithSharedAssetsInformation,
  useManageSharedAssets
} from 'hooks/use-manage-shared-assets'
import { muiTheme } from 'mui-theme'
import { SharedAssetRequestStatus } from 'types/api/sharedAssets/requests'
import { naturalSortBy } from 'utils/list'
import {
  defaultColumnProps,
  defaultGridProps,
  getGridStyles
} from 'utils/mui-components/data-grid-utils'

import type { OwnedK8sClusterAsset } from 'types/api/sharedAssets/sharedAssets'

const ManageClusterAccessTeamsListEmptyState: React.FC = () => {
  return (
    <GridOverlay>
      <Typography>No teams found</Typography>
    </GridOverlay>
  )
}

export const ManageClusterAccessTeamsList: React.FC<{
  cluster: OwnedK8sClusterAsset
}> = ({ cluster }) => {
  const [searchParams] = useSearchParams()
  const searchParamsValue = searchParams.get(SEARCH_PARAMS_KEY) ?? ''
  const [searchValue, setSearchValue] = useDebouncedSearchParams({
    initialSearchValue: searchParamsValue
  })

  const { getTeamsListWithClusterAccessInformation } = useManageSharedAssets()
  const { isOpen, activeItem, handleRowClick, handleOpen, handleClose } =
    useDrawerControlForDataGrid<{ identifier: string }>()

  const teamsListWithClusterAccessInformation =
    getTeamsListWithClusterAccessInformation(cluster.assetId)

  const filteredTeamsListWithClusterAccessInformation = useMemo(() => {
    if (!searchParamsValue) {
      return teamsListWithClusterAccessInformation
    }

    return matchSorter(
      teamsListWithClusterAccessInformation,
      searchParamsValue,
      {
        keys: [
          'name',
          // Allow searching by shared namespaces
          (item) =>
            item.requests[SharedAssetRequestStatus.APPROVED].map(
              (request) => request.namespace
            )
        ],
        sorter: (rankedItems) =>
          // Maintain the initial team sorting order:
          // 1. Teams with incoming requests (REQUESTED first)
          // 2. Teams with approved requests (APPROVED second)
          // 3. Team name (alphabetically)
          naturalSortBy(rankedItems, [
            ({ item }) =>
              item.requests[SharedAssetRequestStatus.REQUESTED].length > 0
                ? 0
                : 1,
            ({ item }) =>
              item.requests[SharedAssetRequestStatus.APPROVED].length > 0
                ? 0
                : 1,
            'name'
          ]),
        threshold: matchSorter.rankings.CONTAINS
      }
    )
  }, [searchParamsValue, teamsListWithClusterAccessInformation])

  const columns: GridColumns<TeamWithSharedAssetsInformation> = useMemo(() => {
    return [
      {
        ...defaultColumnProps,
        field: 'name',
        headerName: 'Team Name',
        flex: 1,
        renderCell: ({ row }) => {
          const { identifier, name, requests } = row

          return (
            <Link onClick={() => handleOpen({ identifier })}>
              <Stack
                alignItems="center"
                direction="row"
                sx={{ columnGap: 0.5 }}
              >
                {requests[SharedAssetRequestStatus.REQUESTED].length > 0 && (
                  <Tooltip
                    placement="top"
                    title="A team is waiting for namespace sharing approval"
                  >
                    <Icon
                      color={muiTheme.palette.primary.main}
                      data-testid="pending-cluster-access-request-icon"
                      icon="solidCirclesOverlap"
                    />
                  </Tooltip>
                )}
                <TruncatedTextWithTooltip
                  tooltipProps={{ title: name }}
                  variant="inherit"
                >
                  {name}
                </TruncatedTextWithTooltip>
              </Stack>
            </Link>
          )
        }
      },
      {
        ...defaultColumnProps,
        field: 'sharedNamespaces',
        headerName: 'Number of Shared Namespaces',
        flex: 1,
        renderCell: ({ row }) => {
          const { requests } = row

          return (
            <Typography variant="inherit">
              {requests[SharedAssetRequestStatus.APPROVED].length}
            </Typography>
          )
        }
      }
    ]
  }, [handleOpen])

  const activeTeam = teamsListWithClusterAccessInformation.find(
    (team) => team.identifier === activeItem?.identifier
  )

  return (
    <>
      <Drawer
        anchor="right"
        open={isOpen}
        PaperProps={{
          sx: {
            padding: 6,
            height: '100%',
            width: 800
          }
        }}
        onClose={handleClose}
      >
        {activeTeam && (
          <ManageClusterAccessDrawerContent
            activeTeam={activeTeam}
            cluster={cluster}
            handleCloseDrawer={handleClose}
          />
        )}
      </Drawer>
      <Stack sx={{ rowGap: 4 }}>
        <SearchField
          data-testid="manage-cluster-access-search"
          helperText="Search by team or shared namespace"
          InputProps={{ sx: { width: SEARCH_INPUT_FIELD_WIDTH } }}
          label="Search"
          value={searchValue}
          onChange={setSearchValue}
        />
        <Box data-testid="manage-cluster-access-teams-list">
          <DataGrid
            {...defaultGridProps}
            columns={columns}
            components={{
              NoRowsOverlay: ManageClusterAccessTeamsListEmptyState
            }}
            getRowId={(value) => value.identifier}
            rows={filteredTeamsListWithClusterAccessInformation}
            sx={getGridStyles()}
            onRowClick={handleRowClick}
          />
        </Box>
      </Stack>
    </>
  )
}
